/**
 * Table header and body cells that handle column alignment.
 */

import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import Clickable from "@khanacademy/wonder-blocks-clickable";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {semanticColor} from "@khanacademy/wonder-blocks-tokens";
import Tooltip from "@khanacademy/wonder-blocks-tooltip";

import type {ColumnDescriptor, DescriptorId, Row, Sort} from "./types.ts";

import iconChevronDown from "./chevron-down.svg";

interface TableHeaderCellProps {
    // All of the information needed to render the column for this cell.
    column: ColumnDescriptor;
    // This column's index.
    columnIndex: number;
    // The id of the column that is currently being sorted.
    activeSortColumn: DescriptorId | null | undefined;
    // The direction of the column sort that is currently active.
    activeSortDirection: Sort | null | undefined;
    useMultipleLineColumnLabel?: boolean;
    onClick: () => void;
}

export const TableHeaderCell = (
    props: TableHeaderCellProps,
): React.ReactNode => {
    const {
        column,
        columnIndex,
        activeSortColumn,
        activeSortDirection,
        useMultipleLineColumnLabel,
        onClick,
    } = props;

    const renderSortableColumnLabel = ({
        hovered,
        focused,
    }: Readonly<{
        focused: boolean;
        hovered: boolean;
    }>): React.ReactNode => {
        const selected = column.id === activeSortColumn;
        return (
            <div
                className={css(
                    styles.label,
                    styles.sortable,
                    column.align === "left" && styles.sortableLeftAlign,
                    column.align === "center" && styles.sortableCenterAlign,
                    column.align === "right" && styles.sortableRightAlignHeader,
                    selected && styles.sortableSelected,
                    !useMultipleLineColumnLabel && styles.singleLine,
                    !!column.tooltipText && styles.labelWithTooltip,
                    (hovered || focused) && styles.highlighted,
                )}
            >
                {column.label}
                <PhosphorIcon
                    icon={iconChevronDown}
                    size="small"
                    className={css(
                        styles.chevron,
                        activeSortDirection === "DESC" && styles.chevronUp,
                        !selected && styles.hidden,
                        !selected &&
                            (hovered || focused) &&
                            styles.highlightedChevron,
                    )}
                />
            </div>
        );
    };

    const sortable = column.sortKeyFn != null || column.onSort != null;
    const testId = `table-header-cell-${columnIndex}`;

    const tableHeaderCell: React.ReactElement<any> = sortable ? (
        <Clickable
            onClick={onClick}
            testId={testId}
            hideDefaultFocusRing={true}
        >
            {renderSortableColumnLabel}
        </Clickable>
    ) : (
        <div className={css(styles.label)} data-testid={testId}>
            {column.label}
        </div>
    );

    if (column.tooltipText) {
        return (
            <Tooltip content={column.tooltipText}>{tableHeaderCell}</Tooltip>
        );
    }

    return tableHeaderCell;
};

interface TableBodyCellProps {
    // All of the information needed to render the column for this cell.
    column: ColumnDescriptor;
    // All of the data needed to render a single row of the table.
    row: Row;
    // Whether the row this cell is in is disabled
    // eslint-disable-next-line react/no-unused-prop-types -- refactored this component. not sure why it's unused, but I'll leave it for now
    disabled: boolean;
    // the hover state of the row
    hovered: boolean;
    // Index of the row
    rowIndex: number;
}

export const TableBodyCell = (props: TableBodyCellProps): React.ReactNode => {
    const {column, row, hovered, rowIndex} = props;

    const sortable = column.sortKeyFn != null || column.onSort != null;

    const style = [
        sortable && column.align === "right" && styles.sortableRightAlign,
    ];

    return (
        <div className={css(...style)}>
            {column.render(row, hovered, rowIndex)}
        </div>
    );
};

const chevronSpacing = 6;
const chevronWidth = 8;
const labelLeftPadding = 3;

const styles = StyleSheet.create({
    // Text styles
    label: {
        // This is deliberately a new typography style used nowhere else
        fontSize: 12,
        lineHeight: "15px",
        textTransform: "uppercase",
        color: semanticColor.core.foreground.neutral.strong,
        display: "inline-flex",
        alignItems: "center",
        position: "relative",
        verticalAlign: "bottom",
    },
    singleLine: {
        whiteSpace: "nowrap",
    },
    labelWithTooltip: {
        textDecoration: "underline",
        textDecorationStyle: "dotted",
        textDecorationColor: semanticColor.core.foreground.neutral.subtle,
    },

    // Sortable header styles
    sortable: {
        position: "relative",
        cursor: "pointer",
        padding: labelLeftPadding,
        paddingInlineEnd: labelLeftPadding + chevronSpacing + chevronWidth,
    },
    sortableLeftAlign: {
        marginInlineStart: -labelLeftPadding,
    },
    sortableCenterAlign: {
        marginInlineStart: chevronSpacing + chevronWidth,
        marginInlineEnd: 0,
    },
    sortableRightAlignHeader: {
        textAlign: "end",
    },
    sortableSelected: {
        fontWeight: "bold",
    },
    highlighted: {
        background: semanticColor.core.background.base.subtle,
    },

    // Chevron styles
    chevron: {
        position: "absolute",
        insetInlineEnd: labelLeftPadding,
        width: chevronWidth,
    },
    chevronUp: {
        transform: "scaleY(-1)",
    },
    hidden: {
        visibility: "hidden",
    },
    highlightedChevron: {
        visibility: "visible",
        // We reset the sort direction to ASC when the user clicks not selected
        // sort column. So when we highlight the column label, we want to show
        // the chevronDown (which is the default one without any transform) no
        // matter what.
        transform: "none",
    },

    // Table body cell styles
    sortableRightAlign: {
        marginInlineEnd: labelLeftPadding + chevronSpacing + chevronWidth,
    },
});
